import React from 'react';
import { addDays, addWeeks, startOfWeek } from 'date-fns';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import monthLabels from 'helpers/monthLabels';
import useOrganizationStartWeekDay from 'hooks/useOrganizationStartWeekDay';
import viewDaysCount from '../consts';

interface ScheduleNavigatorProps {
  currentDate: Date;
  setCurrentDate: React.Dispatch<React.SetStateAction<Date>>;
}

export default function ScheduleNavigator({
  currentDate,
  setCurrentDate,
}: ScheduleNavigatorProps) {
  const { t } = useTranslation();
  const startWeekDay = useOrganizationStartWeekDay();
  const currentDateYear = currentDate.getFullYear();
  const monthOfFirstDayCurrentDate = currentDate.getMonth();
  const monthOfLastDayCurrentDate = addDays(
    currentDate,
    viewDaysCount,
  ).getMonth();

  const firstDayMonthLabel =
    monthLabels[parseInt(monthOfFirstDayCurrentDate.toString(), 10)];
  const lastDayMonthLabel =
    monthLabels[parseInt(monthOfLastDayCurrentDate.toString(), 10)];

  const handleTodayClick = () => {
    setCurrentDate(
      startOfWeek(new Date(), {
        weekStartsOn: startWeekDay,
      }),
    );
  };

  const handlePrevClick = () => {
    setCurrentDate((prevDate) => addWeeks(prevDate, -1));
  };

  const handleNextClick = () => {
    setCurrentDate((prevDate) => addWeeks(prevDate, 1));
  };

  const monthLabel =
    firstDayMonthLabel === lastDayMonthLabel
      ? t(firstDayMonthLabel)
      : `${t(firstDayMonthLabel)}-${t(lastDayMonthLabel)}`;

  return (
    <Stack
      alignItems="center"
      direction="row"
      spacing={1}
      p={3}
      position="sticky"
      top="8rem"
      bgcolor="background.list"
    >
      <Button onClick={handleTodayClick} variant="outlined">
        <Typography color="secondary.main" variant="buttonMedium">
          {t('label.today')}
        </Typography>
      </Button>
      <Stack direction="row">
        <IconButton onClick={handlePrevClick}>
          <ChevronLeftIcon sx={{ fontSize: '2rem' }} />
        </IconButton>
        <IconButton onClick={handleNextClick}>
          <ChevronRightIcon sx={{ fontSize: '2rem' }} />
        </IconButton>
      </Stack>
      <Typography variant="h6" sx={{ userSelect: 'none' }}>
        {monthLabel} {currentDateYear}
      </Typography>
    </Stack>
  );
}
